/**
 * useFoodImages — Fetches carousel images from backend.
 * Returns array: user photos + Google Places + curated fallbacks + static map.
 */
import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const imageCache = {};

function isRealExternalImage(url) {
  if (!url) return false;
  return url.startsWith('http');
}

export function useFoodImages(dishName, existingImage, restaurant = '', location = '', cuisine = '') {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If existing image is a real external URL, use it as the first slide
    const existing = isRealExternalImage(existingImage)
      ? [{ url: existingImage, source: 'existing', attribution: '', type: 'photo' }]
      : [];

    if (!dishName) {
      setImages(existing);
      setLoading(false);
      return;
    }

    const key = `${dishName.toLowerCase().trim()}_${(restaurant || '').toLowerCase().trim()}`;
    if (imageCache[key]) {
      const merged = dedupeImages([...existing, ...imageCache[key]]);
      setImages(merged);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ q: dishName });
        if (restaurant) params.append('restaurant', restaurant);
        if (location) params.append('location', location);
        if (cuisine) params.append('cuisine', cuisine);
        const res = await fetch(`${API_URL}/api/food-images/search?${params}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          const fetched = data.images || [];
          imageCache[key] = fetched;
          const merged = dedupeImages([...existing, ...fetched]);
          setImages(merged);
        }
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();

    return () => { cancelled = true; };
  }, [dishName, existingImage, restaurant, location, cuisine]);

  return { images, loading };
}

function dedupeImages(imgs) {
  const seen = new Set();
  return imgs.filter(img => {
    if (!img.url || seen.has(img.url)) return false;
    seen.add(img.url);
    return true;
  });
}

// Keep old single-image hook for backward compat with other variants
export function useFoodImage(dishName, existingImage, restaurant = '') {
  const { images, loading } = useFoodImages(dishName, existingImage, restaurant);
  const photo = images.find(i => i.type === 'photo');
  return {
    src: photo?.url || null,
    source: photo?.source || 'none',
    attribution: photo?.attribution || '',
    loading,
    handleError: () => {},
  };
}
